import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

class PromotionalOfferSelector extends HTMLElement {
  #scope;
  #productForm;
  #variantId;
  #variantPrice;
  #variantAvailable;
  #variantInventory;
  #variantInventoryManaged;
  #variantInventoryPolicy;
  #onPrepareSubmit;
  #onVariantUpdate;
  #onChange;

  connectedCallback() {
    this.#scope = this.closest('.product-details');
    this.#productForm = this.#scope?.querySelector('product-form-component');
    this.#variantId = this.dataset.variantId || '';
    this.#variantPrice = Number(this.dataset.variantPrice) || 0;
    this.#variantAvailable = this.dataset.variantAvailable === 'true';
    this.#variantInventory = Number(this.dataset.variantInventory);
    this.#variantInventoryManaged = this.dataset.variantInventoryManaged === 'true';
    this.#variantInventoryPolicy = this.dataset.variantInventoryPolicy || '';

    const groupName = `promotional-offer-${this.dataset.sectionId}-${Math.random().toString(36).slice(2)}`;
    this.radios.forEach((radio) => (radio.name = groupName));

    this.#onPrepareSubmit = (event) => this.prepareSubmit(event);
    this.#onVariantUpdate = (event) => this.handleVariantUpdate(event);
    this.#onChange = (event) => this.handleChange(event);
    this.#scope?.addEventListener('product-form:prepare-submit', this.#onPrepareSubmit);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.addEventListener('change', this.#onChange);

    this.ensureSelection();
    this.updateChoiceAvailability();
    this.updateRegularTotals();
    this.syncQuantity();
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener('product-form:prepare-submit', this.#onPrepareSubmit);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.removeEventListener('change', this.#onChange);
  }

  get radios() {
    return Array.from(this.querySelectorAll('[data-offer-choice] > input[type="radio"]'));
  }

  get selectedChoice() {
    return this.querySelector('[data-offer-choice]:has(> input[type="radio"]:checked)');
  }

  ensureSelection() {
    if (this.radios.some((radio) => radio.checked && !radio.disabled)) return;
    const preferred = this.radios.find((radio) => radio.closest('[data-offer-choice]')?.dataset.defaultSelected === 'true' && !radio.disabled);
    const fallback = this.radios.find((radio) => !radio.disabled);
    const behavior = this.dataset.defaultBehavior || 'marked';
    const selection = behavior === 'none' ? undefined : preferred || (behavior === 'first' ? fallback : undefined);
    if (selection) selection.checked = true;
  }

  handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    if (target.matches('[data-companion-variant]')) {
      const choice = target.closest('[data-offer-choice]');
      const radio = choice?.querySelector('input[type="radio"]');
      if (radio && !radio.disabled) radio.checked = true;
      this.updateRegularTotals();
    }

    this.syncQuantity();
    const title = this.selectedChoice?.querySelector('.promotional-offer-choice__title')?.textContent?.trim();
    const status = this.querySelector('.promotional-offer-selector__status');
    if (status && title) status.textContent = `${title} selected`;
  }

  syncQuantity() {
    if (this.dataset.allowCustomQuantity === 'true') return;
    const choice = this.selectedChoice;
    const quantity = Number(choice?.dataset.currentQuantity) || 1;
    const input = this.#productForm?.querySelector('input[name="quantity"]');
    if (input) {
      input.value = String(quantity);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.#productForm) this.#productForm.dataset.quantityDefault = String(quantity);
  }

  prepareSubmit(event) {
    const choice = this.selectedChoice;
    if (!choice) {
      event.detail.error = 'Choose an offer before adding to cart.';
      return;
    }
    if (!this.#variantId || !this.#variantAvailable) return;

    const configuredQuantity = Number(choice.dataset.currentQuantity) || 1;
    const nativeQuantity = Number(this.#productForm?.querySelector('input[name="quantity"]')?.value);
    const currentQuantity = this.dataset.allowCustomQuantity === 'true' && nativeQuantity > 0 ? nativeQuantity : configuredQuantity;
    const items = [{ id: Number(this.#variantId), quantity: currentQuantity }];
    const companion = choice.querySelector('[data-companion-variant]');

    if (companion) {
      const companionId = companion.value;
      const option = companion instanceof HTMLSelectElement ? companion.selectedOptions[0] : companion;
      const companionQuantity = Number(choice.dataset.companionQuantity) || 1;
      const enoughInventory = option?.dataset.inventoryManaged !== 'true'
        || option?.dataset.inventoryPolicy !== 'deny'
        || Number(option?.dataset.inventory) >= companionQuantity;
      if (!companionId || option?.dataset.available !== 'true' || !enoughInventory) {
        event.detail.error = 'Choose an available companion option before adding this offer.';
        return;
      }
      items.push({ id: Number(companionId), quantity: companionQuantity });
    }

    event.detail.items = items;
  }

  handleVariantUpdate(event) {
    if (String(event.detail?.data?.productId) !== String(this.dataset.productId)) return;
    const variant = event.detail?.resource;
    const renderedSelector = event.detail?.data?.html?.querySelector?.(`promotional-offer-selector[data-product-id="${this.dataset.productId}"]`);
    this.#variantId = variant?.id ? String(variant.id) : '';
    this.#variantPrice = Number(variant?.price) || 0;
    this.#variantAvailable = Boolean(variant?.available);
    this.#variantInventory = Number(renderedSelector?.dataset.variantInventory);
    this.#variantInventoryManaged = renderedSelector?.dataset.variantInventoryManaged === 'true';
    this.#variantInventoryPolicy = renderedSelector?.dataset.variantInventoryPolicy || '';
    this.dataset.variantId = this.#variantId;
    this.dataset.variantPrice = String(this.#variantPrice);
    this.dataset.variantAvailable = String(this.#variantAvailable);
    this.updateChoiceAvailability();
    this.updateRegularTotals();
    this.ensureSelection();
    this.syncQuantity();
  }

  updateChoiceAvailability() {
    for (const choice of this.querySelectorAll('[data-offer-choice]')) {
      const radio = choice.querySelector('input[type="radio"]');
      const companion = choice.querySelector('[data-companion-variant]');
      const companionQuantity = Number(choice.dataset.companionQuantity) || 0;
      const companionHasAvailability = !companion || Array.from(companion.options || [companion]).some((option) => {
        const enoughInventory = option.dataset.inventoryManaged !== 'true'
          || option.dataset.inventoryPolicy !== 'deny'
          || Number(option.dataset.inventory) >= companionQuantity;
        return option.dataset.available === 'true' && enoughInventory;
      });
      const currentQuantity = Number(choice.dataset.currentQuantity) || 1;
      const enoughCurrentInventory = !this.#variantInventoryManaged
        || this.#variantInventoryPolicy !== 'deny'
        || this.#variantInventory >= currentQuantity;
      const available = choice.dataset.active === 'true' && this.#variantAvailable && enoughCurrentInventory && companionHasAvailability;
      radio.disabled = !available;
      choice.classList.toggle('promotional-offer-choice--disabled', !available);
    }
  }

  updateRegularTotals() {
    for (const choice of this.querySelectorAll('[data-offer-choice]')) {
      const output = choice.querySelector('[data-regular-total]');
      if (!output) continue;
      const currentTotal = this.#variantPrice * (Number(choice.dataset.currentQuantity) || 1);
      const companion = choice.querySelector('[data-companion-variant]');
      const option = companion instanceof HTMLSelectElement ? companion.selectedOptions[0] : companion;
      const companionTotal = (Number(option?.dataset.price) || 0) * (Number(choice.dataset.companionQuantity) || 0);
      output.textContent = formatMoney(currentTotal + companionTotal, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
    }
  }
}

if (!customElements.get('promotional-offer-selector')) {
  customElements.define('promotional-offer-selector', PromotionalOfferSelector);
}
